import React, { ReactChild, Fragment } from "react";
import { RouteComponentProps } from "react-router";

import withTitle, { IViewTitleProps } from "../withTitle";
import TabControl from "../TabControl";
import Indicator from "../Indicator";
// import ClickReveal from "../ClickReveal";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { V2 } from "../../Api";
import { formatDateLocal, formatDateAge, formatTelephone, formatDateLocalDayOfWeek } from "../../Utilities";

import "../../form-template.css";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";


interface ITelemedFormViewState {
    isLoading: boolean | string;
    isDeleteDialogOpen: boolean;
    forms: any[];
    formHeaders: string[];
    formContents: any[];
    selectedFormIndex: number;

    name: string;
    timestamp: string;
    locationName: string;
}

interface ITelemedFormViewProps extends RouteComponentProps<any>, IViewTitleProps {

}

class TelemedFormView extends React.Component<ITelemedFormViewProps, ITelemedFormViewState> {
    private FormTypeIdMap: { [id: string]: { name: string, render?: (data: any) => ReactChild } } = {};

    constructor(props: Readonly<ITelemedFormViewProps>) {
        super(props);

        const state: ITelemedFormViewState = {
            isLoading: true,
            isDeleteDialogOpen: false,
            forms: [],
            formHeaders: [],
            formContents: [],
            selectedFormIndex: 0,

            name: "",
            locationName: "",
            timestamp: "",
        };

        this.state = state;

        this.loadData = this.loadData.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
        this.markUnread = this.markUnread.bind(this);
        this.toggleDeletePrompt = this.toggleDeletePrompt.bind(this);
        this.deleteForms = this.deleteForms.bind(this);
        this.handleDeleteSuccess = this.handleDeleteSuccess.bind(this);

        this.formNewPatientPacket = this.formNewPatientPacket.bind(this);
        this.formSocialHistory = this.formSocialHistory.bind(this);
        this.formEPrescribe = this.formEPrescribe.bind(this);
        this.formHipaa = this.formHipaa.bind(this);
        this.formIV = this.formIV.bind(this);

        this.FormTypeIdMap = {
            "hc-npi-1": {
                name: "New Patient Info",
                render: this.formNewPatientPacket
            },
            "hc-sh-1": {
                name: "Social History",
                render: this.formSocialHistory
            },
            "hc-epc-1": {
                name: "E-Prescribing Consent",
                render: this.formEPrescribe
            },
            "hc-hipaad-1": {
                name: "HIPAA Acknowledgement & Disclosure",
                render: this.formHipaa
            },
            "hc-iv-1": {
                name: "IV Form",
                render: this.formIV
            },
        };
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-box">{this.state.isLoading}</div>
            </div>
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" className="text-with-icon" title="Mark as Unread" onClick={this.markUnread}><i className="icon-minus" /><span>Mark as Unread</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="forms:delete">
                            <button type="button" className="text-with-icon" title="Delete this set of forms" onClick={this.toggleDeletePrompt}><i className="icon-trash" /><span>Delete Forms</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onRequestClose={this.toggleDeletePrompt} onConfirm={this.deleteForms} onSuccess={this.handleDeleteSuccess} />
                <TabControl headers={this.state.formHeaders} defaultSelectedIndex={this.state.selectedFormIndex} onTabChanged={this.handleTabChanged}>
                    {this.state.formContents}
                </TabControl>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const formSet = await V2.get<any>(`forms/${this.props.match.params.id}?mode=full&read=true`);

            this.props.viewTitleContext.changeTitle(`Forms \u2014 ${formSet.name}`, "both");

            const formHeaders = [];
            const formContents = [];
            for (const form of formSet.forms) {
                formHeaders.push(this.formTypeToName(form.type));
                formContents.push(this.getFormTemplate(form.type)(JSON.parse(form.content)));
            }

            let defaultTab = 0;
            if (this.props.match.params.form) {
                const tabIndex = formSet.forms.findIndex((a: any) => a.type === this.props.match.params.form);
                if (tabIndex >= 0) {
                    defaultTab = tabIndex;
                }
            }

            this.setState({
                isLoading: false,
                forms: formSet.forms,
                formHeaders,
                formContents,
                selectedFormIndex: defaultTab,

                name: formSet.name,
                locationName: formSet.locationName,
                timestamp: formSet.timestamp
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load forms"
            });
        }
    }

    private formTypeToName(id: string) {
        const form = this.FormTypeIdMap[id];
        return (form && form.name) || id;
    }

    private getFormTemplate(id: string) {
        const form = this.FormTypeIdMap[id];
        return (form && form.render) || ((data: any) => null);
    }

    private handleTabChanged(index: number) {
        const { forms } = this.state;

        const formId = forms[index].type;
        const newUrl = this.props.match.params.form
            ? this.props.match.url.replace(this.props.match.params.form, formId)
            : `${this.props.match.url}/${formId}`;

        this.props.history.replace(newUrl);
    }

    private async markUnread() {
        try {
            await V2.post(`forms/${this.props.match.params.id}/unread`);

            this.props.history.goBack();
        }
        catch (e) {
            // Eat error
        }
    }

    private toggleDeletePrompt() {
        this.setState(s => ({
            isDeleteDialogOpen: !s.isDeleteDialogOpen
        }));
    }

    private async deleteForms() {
        await V2.delete(`forms/${this.props.match.params.id}`);
    }

    private handleDeleteSuccess() {
        this.props.history.goBack();
    }

    static historyProblemToText(problem: string) {
        switch (problem) {
            case "acute-infections": return "Acute Infections";
            case "angina": return "Angina";
            case "bleeding-tendencies": return "Bleeding tendencies";
            case "cancer": return "Cancer";
            case "cesarean-section": return "Cesarean Section";
            case "depression": return "Depression";
            case "diabetes": return "Diabetes";
            case "epilepsy": return "Epilepsy";
            case "hiv": return "HIV";
            case "hepatitis": return "Hepatitis";
            case "hereditary-defects": return "Hereditary Defects";
            case "high-blood-pressure": return "High Blood Pressure";
            case "hyperthyroidism": return "Hyperthyroidism";
            case "hypothyroidism": return "Hypothyroidism";
            case "irritable-bowel-syndrome": return "Irritable Bowel Syndrome";
            case "kidney-trouble": return "Kidney trouble";
            case "liver-disease": return "Liver Disease";
            case "mental-illness": return "Mental Illness";
            case "mitral-valve-prolapse": return "Mitral Valve Prolapse";
            case "heart-attack": return "Previous Heart Attack";
            case "stroke": return "Stroke";
            case "suicide": return "Suicide";
            case "tuberculosis": return "Tubercluosis";
            case "venereal-disease": return "Venereal Disease";
            default: return problem;
        }
    }

    static problemToText(problem: string) {
        switch (problem) {
            case "diabetes": return "Diabetes";
            case "blood-clot": return "Blood Clot";
            case "mental-illness": return "Mental Illness";
            case "high-blood-pressure": return "High Blood Pressure";
            case "heart-disease": return "Heart Disease";
            case "increased-cholesterol": return "Increased Cholesterol";
            case "stroke": return "Stroke";
            case "birth-defects": return "Birth Defects";
            case "ovarian-cancer": return "Ovarian Cancer";
            case "uterine-cancer": return "Uterine Cancer";
            case "breast-cancer": return "Breast Cancer";
            case "other-cancer": return "Other Cancers";
            case "other-problems": return "Other Problems";
            default: return problem;
        }
    }

    static symptomToText(problem: string) {
        switch (problem) {
            case "acute-asthma-attacks": return "Acute Asthma Attacks";
            case "allergic-rhinitis": return "Allergic Rhinitis";
            case "arthritis": return "Arthritis";
            case "asthma": return "Asthma";
            case "cancer": return "Cancer";
            case "chronic-fatigue-syndrome": return "Chronic Fatigue Syndrome";
            case "dehydration": return "Dehydration";
            case "depression": return "Depression";
            case "diabetes": return "Diabetes";
            case "fibromyalgia": return "Fibromyalgia";
            case "headaches": return "Headaches";
            case "hormone-imbalance": return "Hormone Imbalance";
            case "hypertension": return "Hypertension";
            case "insomnia": return "Insomnia";
            case "joint-pain": return "Joint Pain";
            case "light-headedness": return "Light Headedness";
            case "lyme-disease": return "Lyme Disease";
            case "migraines": return "Migraines";
            case "muscle-pain": return "Muscle Pain";
            case "muscle-spasms": return "Muscle Spasms";
            case "obesity": return "Obesity";
            case "rheumatoid-arthritis": return "Rheumatoid Arthritis";
            case "sinusitis": return "Sinusitis";
            case "sleep-disturbance": return "Sleep Disturbance";
            case "tension-headaches": return "Tension Headaches";
            case "upper-respiratory-tract-infection": return "Upper Respiratory Tract Infection";
            case "vitamin-b-deficiency": return "Vitamin B Deficiency";
            case "vitamin-c-deficiency": return "Vitamin C Deficiency";
            case "weakness": return "Weakness";
            default: return problem;
        }
    }

    static hipaaTelephoneToText(allow: string) {
        switch (allow) {
            case "detailed": return "OK to leave message with detailed information";
            case "appointment-only": return "Leave message with appointment information only";
            case "callback": return "Leave message with call back numbers only";
            default: return allow;
        }
    }

    static birthTypeToText(type: string) {
        switch (type) {
            case "vaginal": return "Vaginal birth";
            case "csection": return "Cesarean section";
            default: return type;
        }
    }

    private formNewPatientPacket(data: any) {
        return <div key="npi-template" className="form-template-body">
            <h3>Basics</h3>
            <dl>
                <div>
                    <dt>Date Filled Out</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Full Name</dt>
                    <dd>{data["name-full"]}</dd>
                </div>
                <div>
                    <dt>Date of Birth</dt>
                    <dd>{formatDateLocal(data["dob"])} ({formatDateAge(data["dob"])} years old)</dd>
                </div>
                {/*<div>
                    <dt>Social Security Number</dt>
                    <dd><ClickReveal mask={`xxx-xx-${data["ssn"].substr(-4)}`} hint={<small>(Click to show)</small>}>{data["ssn"]}</ClickReveal></dd>
                </div>*/}
            </dl>

            {/*<h3>Spouse</h3>
            <dl>
                <div>
                    <dt>Name</dt>
                    <dd>{data["spouse-name-full"] || "No Spouse"}</dd>
                </div>
                <div>
                    <dt>Date of Birth</dt>
                    <dd>{formatDateLocal(data["spouse-dob"]) || "N/A"} {!!data["spouse-dob"] && ` (${formatDateAge(data["spouse-dob"])} years old)`}</dd>
                </div>
            </dl>*/}

            <h3>Contact</h3>
            <dl>
                <div>
                    <dt>Email Address</dt>
                    <dd>{data["email"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Home Phone</dt>
                    <dd>{formatTelephone(data["home-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Cell Phone</dt>
                    <dd>{formatTelephone(data["cell-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Work Phone</dt>
                    <dd>{formatTelephone(data["work-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Home Address</dt>
                    <div>
                        <dd>{data["address-line-1"] || <span className="data-missing">Not Specified</span>}</dd>
                        <dd>{data["address-city"]}, {data["address-state"]} {data["address-zip"]}</dd>
                    </div>
                </div>
            </dl>

            <h3>Employment</h3>
            <dl>
                <div>
                    <dt>Occupation</dt>
                    <dd>{data["employer-occupation"] || <span>Not Employed</span>}</dd>
                </div>
                {
                    !!data["employer-name"] && !!data["employer-occupation"]
                        ? <>
                            <div>
                                <dt>Employer</dt>
                                <dd>{data["employer-name"] || <span className="data-missing">Not Specified</span>}</dd>
                            </div>
                            <div>
                                <dt>Employer Phone</dt>
                                <dd>{formatTelephone(data["employer-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                            </div>
                            <div>
                                <dt>Address</dt>
                                <div>
                                    <dd>{data["employer-address-line-1"] || <span className="data-missing">Not Specified</span>}</dd>
                                    <dd>{data["employer-address-city"]}, {data["employer-address-state"]} {data["employer-address-zip"]}</dd>
                                </div>
                            </div>
                        </>
                        : null
                }
            </dl>

            <h3>Physicians & Pharmacy</h3>
            <dl>
                <div>
                    <dt>Primary Physician</dt>
                    <dd>{data["physician-primary"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Referring Physician</dt>
                    <dd>{data["physician-referring"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Pharmacy</dt>
                    <dd>{data["pharmacy"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
            </dl>

            <h3>Emergency Contact</h3>
            <dl>
                <div>
                    <dt>Name</dt>
                    <dd>{data["emergency-contact"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Phone Number</dt>
                    <dd>{formatTelephone(data["emergency-contact-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                </div>
            </dl>

            <h2>Responsible Party</h2>
            <dl>
                {!!data["guarantor-name-full"]
                    ? <>
                        <div>
                            <dt>Full Name</dt>
                            <dd>{formatTelephone(data["guarantor-name-full"])}</dd>
                        </div>
                        <div>
                            <dt>Date of Birth</dt>
                            <dd>{formatDateLocal(data["guarantor-dob"])}  ({formatDateAge(data["guarantor-dob"])} years old)</dd>
                        </div>
                        <div>
                            <dt>Relation to Patient</dt>
                            <dd>{data["guarantor-relationship"] || <span className="data-missing">Not Specified</span>}</dd>
                        </div>
                    </>
                    : <div>
                        <dt />
                        <dd>Patient is responsible for themselves</dd>
                    </div>}
            </dl>

            {!!data["guarantor-name-full"]
                ? <>
                    <h3>Responsible Party &mdash; Address</h3>
                    <dl>
                        {!!data["guarantor-address-line-1"]
                            ? <>
                                <div>
                                    <dt>Address</dt>
                                    <div>
                                        <dd>{data["guarantor-address-line-1"]}</dd>
                                        <dd>{data["guarantor-address-city"]}, {data["guarantor-address-state"]} {data["guarantor-address-zip"]}</dd>
                                    </div>
                                </div>
                            </>
                            : <div>
                                <dt>Address</dt>
                                <dd>Same as patient</dd>
                            </div>}
                    </dl>


                    <h3>Responsible Party &mdash; Employment</h3>
                    <dl>
                        <div>
                            <dt>Occupation</dt>
                            <dd>{data["guarantor-employer-occupation"] || "Not Employed"}</dd>
                        </div>
                        {!!data["guarantor-employer-occupation"]
                            ? <>
                                <div>
                                    <dt>Employer</dt>
                                    <dd>{data["guarantor-employer-name"]}</dd>
                                </div>
                                <div>
                                    <dt>Employer Phone</dt>
                                    <dd>{formatTelephone(data["guarantor-employer-phone"]) || <span className="data-missing">Not Specified</span>}</dd>
                                </div>
                                <div>
                                    <dt>Address</dt>
                                    <div>
                                        <dd>{data["guarantor-employer-address-line-1"] || <span className="data-missing">Not Specified</span>}</dd>
                                        <dd>{data["guarantor-employer-address-city"]}, {data["guarantor-employer-address-state"]} {data["guarantor-employer-address-zip"]}</dd>
                                    </div>
                                </div>
                            </>
                            : null}
                    </dl>
                </>
                : null
            }
        </div>;
    }

    private formSocialHistory(data: any) {
        return <div key="sh-template" className="form-template-body">
            <h3>Basics</h3>
            <dl>
                <div>
                    <dt>Date Filled Out</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Full Name</dt>
                    <dd>{data["name-full"]}</dd>
                </div>
                <div>
                    <dt>Date of Birth</dt>
                    <dd>{formatDateLocal(data["dob"])} ({formatDateAge(data["dob"])} years old)</dd>
                </div>
                <div>
                    <dt>Height</dt>
                    <dd>{data["height"]}</dd>
                </div>
                <div>
                    <dt>Occupation</dt>
                    <dd>{data["employer-occupation"] || "Not Employed"}</dd>
                </div>
                <div>
                    <dt>Allergies</dt>
                    <dd>{data["allergies"] || "None listed"}</dd>
                </div>
                <div>
                    <dt>Marital Status</dt>
                    <dd>{data["marital-status"]}</dd>
                </div>
            </dl>

            <h3>Drugs & Alcohol</h3>
            <dl>
                <div>
                    <dt>Alcohol</dt>
                    <dd>{data["alcohol-use"]}</dd>
                </div>
                <div>
                    <dt>Smoking</dt>
                    {data["smoking"] === "yes"
                        ? <div>
                            <dd>Yes</dd>
                            <dd>{data["smoking-amount"] || <span className="data-missing">Amount not specified</span>}</dd>
                        </div>
                        : <dd>No</dd>
                    }
                </div>
                <div>
                    <dt>Ilicit Drugs</dt>
                    {data["drug-use"] === "yes"
                        ? <div>
                            <dd>Yes</dd>
                            <dd>{data["drug-kinds"] || <span className="data-missing">Kinds not specified</span>}</dd>
                        </div>
                        : <dd>No</dd>
                    }
                </div>
            </dl>

            <h3>Past Medical History</h3>
            <dl>
                <div>
                    <dt>Had DVT?</dt>
                    <dd>{data["dvt"] ? "Yes" : "No"}</dd>
                </div>
                <div>
                    <dt>Had Abnormal Pap?</dt>
                    <dd>{data["abnormal-pap"] ? "Yes" : "No"}</dd>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Has Had</dt>
                    <div>
                        {(data["medical-history"] || []).length > 0
                            ? (data["medical-history"] || []).map((a: string) => <dd key={a}>
                                {TelemedFormView.historyProblemToText(a)}
                            </dd>)
                            : <dd><span className="data-missing">Nothing listed</span></dd>}
                    </div>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Past Surgeries</dt>
                    <div>
                        {(data["surgery-list"] || []).length > 0
                            ? (data["surgery-list"] || []).map((a: string) => <dd key={a}>
                                {a}
                            </dd>)
                            : <dd><span className="data-missing">Nothing listed</span></dd>}
                    </div>
                </div>
            </dl>

            <h3>Medications</h3>
            <dl>
                <div>
                    <dt>Medication List</dt>
                    <div>
                        {(data["medication-list"] || []).map((a: string) => <dd key={a}>
                            {a}
                        </dd>)}
                    </div>
                </div>
            </dl>

            <h3>Family History</h3>
            <dl>
                {(data["family-history"] || []).map((a: any) => <div key={a.id}>
                    <dt>{TelemedFormView.problemToText(a.id)}</dt>
                    <div>
                        <dd>{a.value}</dd>
                        {a.member
                            ? <dd>{a.member}</dd>
                            : null}
                    </div>
                </div>)}
            </dl>

            <h3>Obstetrical History</h3>
            <dl>
                <div>
                    <dt>Pregnancies</dt>
                    <dd>{data["pregnancy-count"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt>Living Children</dt>
                    <dd>{data["child-living-count"] || <span className="data-missing">Not Specified</span>}</dd>
                </div>
                <div>
                    <dt />
                    <dd><h3>Pregnancy List</h3></dd>
                </div>
                {data["pregnancy-list"]
                    ? (data["pregnancy-list"] || []).map((a: any, i: number) => <Fragment key={i}>
                        {i > 0 ? <div className="spacer" /> : null}
                        <div>
                            <dt>Date of Birth</dt>
                            <dd>{formatDateLocal(a["dob"])}</dd>
                        </div>
                        <div>
                            <dt>Sex</dt>
                            <dd>{a["sex"] || <span className="data-missing">Not Specified</span>}</dd>
                        </div>
                        <div>
                            <dt>Weight</dt>
                            <dd>{a["weight"] || <span className="data-missing">Not Specified</span>}</dd>
                        </div>
                        <div>
                            <dt>Birth Type</dt>
                            <dd>{a["birth-type"] ? TelemedFormView.birthTypeToText(a["birth-type"]) : <span className="data-missing">Not Specified</span>}</dd>
                        </div>
                        <div>
                            <dt>Complications</dt>
                            <dd>{a["complications"] || "None"}</dd>
                        </div>
                    </Fragment>)
                    : <div>
                        <dt />
                        <dd><span className="data-missing">None listed</span></dd>
                    </div>}

                <h3>Gynecological History</h3>
                <dl>
                    <div>
                        <dt>Age of 1st Period</dt>
                        <dd>{data["gyn-period-age"]
                            ? `${data["gyn-period-age"]} years old`
                            : <span className="data-missing">Not Specified</span>}
                        </dd>
                    </div>
                    <div>
                        <dt>Regular Period</dt>
                        <dd>{data["gyn-period-regular"] ? "Yes" : "No"}</dd>
                    </div>
                    {!data["gyn-period-regular"]
                        ? <div>
                            <dt>Irregular Period Reason</dt>
                            <dd>{data["gyn-period-irregular-reason"] || <span className="data-missing">Not Specified</span>}</dd>
                        </div>
                        : null}
                    <div>
                        <dt>Period Flow</dt>
                        <dd>{data["gyn-period-flow"] || <span className="data-missing">Not Specified</span>}</dd>
                    </div>
                    <div>
                        <dt>Normal Bleed Days</dt>
                        <dd>{data["gyn-period-bleed-days"] || <span className="data-missing">Not Specified</span>}</dd>
                    </div>
                    <div>
                        <dt>Cramping</dt>
                        <dd>{data["gyn-period-cramping"] || <span className="data-missing">Not Specified</span>}</dd>
                    </div>
                    <div className="spacer" />
                    <div>
                        <dt>Has Had STD</dt>
                        <div>
                            <dd>{data["gyn-std"] ? "Yes" : "No"}</dd>
                            {data["gyn-std"] ?
                                <dd>{data["gyn-std-type"] || <span className="data-missing">STD not Specified</span>}</dd>
                                : null
                            }
                        </div>
                    </div>
                    <div>
                        <dt>Lifetime Sexual Partners</dt>
                        <dd>{data["gyn-sexual-partners"] || <span className="data-missing">Not Specified</span>}</dd>
                    </div>
                    <div>
                        <dt>Sexually Active</dt>
                        <dd>{data["gyn-sexually-active"] ? "Yes" : "No"}</dd>
                    </div>
                </dl>
            </dl>
        </div>;
    }

    private formEPrescribe(data: any) {
        return <div key="epi-template" className="form-template-body">
            <h3>Patient</h3>
            <dl>
                <div>
                    <dt>Full Name</dt>
                    <dd>{data["name-full"]}</dd>
                </div>
                <div>
                    <dt>Date of Birth</dt>
                    <dd>{formatDateLocal(data["dob"])}</dd>
                </div>
            </dl>

            <h3>Consent</h3>
            <dl>
                <div>
                    <dt>Date Signed</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
                <div>
                    <dt>Read & Understood</dt>
                    <dd>{data["eprescribe-understand"] ? "Yes" : "No"}</dd>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Consent/Deny</dt>
                    <dd>{data["eprescribe-consent"] === "consent"
                        ? <Indicator icon="check" type="bare" color="green" text="Consent" />
                        : <Indicator icon="times" type="bare" color="red" text="Deny" />}
                    </dd>
                </div>
                <div>
                    <dt>Signed by</dt>
                    <dd>{!!data["eprescribe-representative"]
                        ? data["eprescribe-representative-relation"]
                        : "Patient"}</dd>
                </div>
            </dl>

        </div>;
    }

    private formHipaa(data: any) {
        return <div key="hipaa-template" className="form-template-body">
            <h3>Acknowledgement of Practice's Notice of Privacy Practices</h3>
            <dl>
                <div>
                    <dt>Full Name</dt>
                    <dd>{data["name-full"]}</dd>
                </div>
                <div>
                    <dt>Date of Birth</dt>
                    <dd>{formatDateLocal(data["dob"])}</dd>
                </div>
                <div>
                    <dt>Date</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
            </dl>

            <h3>Designation of Personal Representatives</h3>
            <dl>
                {(data["hipaa-representative-list"] || []).length > 0
                    ? (data["hipaa-representative-list"] || []).map((a: any, i: number) => <Fragment key={i}>
                        {i > 0 ? <div className="spacer" /> : null}
                        <div>
                            <dt>Name</dt>
                            <dd>{a["name"]}</dd>
                        </div>
                        <div>
                            <dt>SSN</dt>
                            <dd>xxx-xx-{a["ssn"]}</dd>
                        </div>
                    </Fragment>)
                    : <div>
                        <dt />
                        <dd>None Listed</dd>
                    </div>
                }
            </dl>

            <h3>Request to Receive Confidential Communications by Alternative Means</h3>
            <dl>
                <div>
                    <dt>Telephone Number</dt>
                    <div>
                        <dd>{formatTelephone(data["hipaa-am-telephone"]) || <span className="data-missing">Not specified</span>}</dd>
                        {data["hipaa-am-telephone"]
                            ? <dd>{TelemedFormView.hipaaTelephoneToText(data["hipaa-am-telephone-allow"])}</dd>
                            : null
                        }
                    </div>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Email</dt>
                    <dd>{formatTelephone(data["hipaa-am-email"]) || <span className="data-missing">Not specified</span>}</dd>
                </div>
                <div>
                    <dt>Physical Address</dt>
                    <div>
                        <dd>{data["hipaa-am-address-line-1"] || <span className="data-missing">Not Specified</span>}</dd>
                        {!!data["hipaa-am-address-line-1"]
                            ? <>
                                <dd>{data["hipaa-am-address-city"]}, {data["hipaa-am-address-state"]} {data["hipaa-am-address-zip"]}</dd>
                                <dd>{data["hipaa-am-address-allow-mail"] ? "OK to mail to address" : "No not mail to address"}</dd>
                            </>
                            : null}
                    </div>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Other methods</dt>
                    <dd>{formatTelephone(data["hipaa-am-other"]) || <span className="data-missing">Not specified</span>}</dd>
                </div>
            </dl>

            <h3>Signature</h3>
            <dl>
                <div>
                    <dt>Full Name</dt>
                    <dd>{data["name-full"]}</dd>
                </div>
                <div>
                    <dt>Signature</dt>
                    <dd>{data["hipaa-signature"]
                        ? <Indicator icon="check" type="bare" color="green" text="Signed Digitally" />
                        : <Indicator icon="times" type="bare" color="red" text="Not Signed" />}
                    </dd>
                </div>
                <div>
                    <dt>Date Signed</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
            </dl>
        </div>;
    }

    private formIV(data: any) {
        return <div key="iv-template" className="form-template-body">
            <h3>IV Sheet</h3>
            <dl>
                <div>
                    <dt>Date Filled Out</dt>
                    <dd>{formatDateLocalDayOfWeek(data["date-signed"])}</dd>
                </div>
                <div className="spacer" />
                <div>
                    <dt>Symptoms</dt>
                    <div>
                        {(data["iv-symptoms"] || []).map((a: any) => <dd key={a}>
                            {TelemedFormView.symptomToText(a)}
                        </dd>)}
                    </div>
                </div>
            </dl>
        </div>;
    }
}

export default withTitle("Received Telemed Forms", TelemedFormView);